<template>
  <div :class="wrapperClass">

    <!-- HEADER -->
    <div class="flex justify-between items-center mb-6">

      <!-- LEFT: Title + Badge -->
      <div class="flex items-center gap-3">

        <h1 :class="titleClass">Dashboard</h1>

        <!-- User Type Badge -->
        <div
            class="user-type-badge"
            :class="`type-${user_type}`"
        >
      <span class="type-icon">
        <template v-if="user_type === 'corporate'">🏢</template>
        <template v-else-if="user_type === 'premium'">💎</template>
        <template v-else>👤</template>
      </span>

          <span class="type-text">
        {{ user_type }}
      </span>
        </div>

      </div>

      <!-- RIGHT: Time -->
      <div class="text-sm opacity-70">
        {{ displayTime }} UTC
      </div>

    </div>



    <!-- 🎞️ IMAGE SLIDER -->
    <div class="mb-8">

      <Swiper
          :centered-slides="true"
          :grab-cursor="true"
          :loop="true"
          :slides-per-view="3"
          :space-between="20"
          :autoplay="{ delay: 2500, disableOnInteraction: false }"
          :pagination="{ clickable: true }"
          class="max-w-screen-lg mx-auto"
      >

        <SwiperSlide
            v-for="(card, index) in cards"
            :key="index"
        >
          <div class="rounded-2xl overflow-hidden shadow-md h-40">
            <img
                :src="card.src"
                class="w-full h-full object-cover"
                alt=""
            />
          </div>
        </SwiperSlide>

      </Swiper>

    </div>



    <!-- TOP CARDS -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

      <div :class="cardClass" class="relative overflow-hidden">

        <div
            v-if="user_type === 'corporate'"
            class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400"
        ></div>

        <div class="flex items-center gap-3">
          <div :class="iconWrapperClass">
            ⏱
          </div>
          <div>
            <div class="text-xs opacity-70">Last Authentication</div>
            <div class="font-semibold">{{ displayTime }}</div>
          </div>
        </div>
      </div>




      <div :class="cardClass" class="relative overflow-hidden">
        <div
            v-if="user_type === 'corporate'"
            class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400"
        ></div>

        <div class="flex items-center gap-3">
          <div :class="iconWrapperClass">⚙️</div>
          <div>
            <div class="text-xs opacity-70">Operations</div>
            <div class="font-semibold">No pending operations</div>
          </div>
        </div>
      </div>

      <div :class="cardClass" class="relative overflow-hidden">

        <div
            v-if="user_type === 'corporate'"
            class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400"
        ></div>


        <div class="flex items-center gap-3">
          <div :class="iconWrapperClass">✉️</div>
          <div>
            <div class="text-xs opacity-70">Messages</div>
            <div class="font-semibold">No new messages</div>
          </div>
        </div>
      </div>

    </div>

    <!-- MAIN GRID -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- LEFT: CHART -->
      <div :class="cardClass">

        <div class="flex justify-between mb-4">
          <h3 :class="sectionTitleClass">Portfolio</h3>
        </div>

        <div class="flex items-center justify-between">
          <canvas ref="chartCanvas" class="w-32 h-32"></canvas>

          <div class="space-y-2 text-sm">
            <div v-for="(value,currency) in currencyTotals" :key="currency" class="flex justify-between">
              <span>{{ currency }}</span>
              <span class="font-semibold">{{ formatNumber(value) }}</span>
            </div>
          </div>
        </div>

      </div>

      <!-- RIGHT: ACCOUNTS -->
      <div class="lg:col-span-2">

        <div class="flex justify-between items-center mb-3">
          <h3 :class="sectionTitleClass">Accounts</h3>
        </div>

        <div :class="cardClass">

          <div
              v-for="item in items"
              :key="item.id"
              :class="listItemClass"
              @click="goToAccount(item)"
          >
            <div>
              <div class="font-semibold">{{ item.account_name }}</div>
              <div class="text-xs opacity-60">{{ item.account_number }}</div>
            </div>

            <div class="font-semibold">
              {{ item.currency }} {{ formatNumber(item.available_balance) }}
            </div>
          </div>

        </div>

      </div>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Chart from 'chart.js/auto'
import api from '@/services/api'
import { formatNumber } from '@/utils/number'
import {useRouter} from "vue-router";

import { Swiper, SwiperSlide } from "swiper/vue"
import "swiper/css"
import "swiper/css/pagination"
import "swiper/css/navigation"

import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper"

SwiperCore.use([Pagination, Navigation, Autoplay])

const user_type = ref('premium')
// standard | premium | corporate

const items = ref([])
const currencyTotals = ref({})
const displayTime = ref('')
const chartCanvas = ref(null)

let chartInstance = null
const router = useRouter()



const cards = ref([])
async function fetchImages() {
  try {
    const { data } = await api.get('/banners', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`
      }
    })

    cards.value = data
  } catch (err) {
    console.error('banner error', err)
  }
}

const goToAccount = (item) => {
  router.push({
    path: '/view/balance',
    query: { account: item.account_number }
  })
}

/* 🎨 THEME SYSTEM */

const theme = {
  standard: {
    wrapper: 'bg-white text-gray-800',
    card: 'bg-gray-100',
    title: 'text-gray-800',
    section: 'text-gray-700',
    item: 'hover:bg-gray-50'
  },
  premium: {
    wrapper: 'bg-gradient-to-br from-indigo-50 to-white text-gray-800',
    card: 'bg-white shadow-md border border-indigo-100',
    title: 'text-indigo-700',
    section: 'text-indigo-600',
    item: 'hover:bg-indigo-50'
  },
  corporate: {
    wrapper: 'bg-gradient-to-br from-gray-50 to-white text-gray-800',
    card: 'bg-white border border-gray-200 shadow-sm hover:shadow-md',
    title: 'text-gray-900 font-semibold tracking-wide',
    section: 'text-gray-500 uppercase text-xs tracking-wider',
    item: 'hover:bg-gray-50'
  }
}

const currentTheme = computed(() => theme[user_type.value])

const wrapperClass = computed(() =>
    `min-h-screen p-6 transition-all duration-300 ${currentTheme.value.wrapper}`
)

const cardClass = computed(() =>
    // `rounded-2xl p-5 transition-all duration-300 ${currentTheme.value.card}`
    `rounded-2xl p-5 transition-all duration-300 ${currentTheme.value.card} backdrop-blur-sm`
)

const titleClass = computed(() =>
    `text-2xl font-semibold ${currentTheme.value.title}`
)

const sectionTitleClass = computed(() =>
    `font-semibold mb-2 ${currentTheme.value.section}`
)

const listItemClass = computed(() =>
    `flex justify-between p-4 border-b border-white/10 cursor-pointer transition ${currentTheme.value.item}`
)

const iconWrapperClass = computed(() => {
  if (user_type.value === 'corporate')
    return 'bg-gray-100 border border-gray-200 p-2 rounded-lg'
  if (user_type.value === 'premium')
    return 'bg-indigo-100 p-2 rounded-lg'
  return 'bg-white p-2 rounded-lg'
})

/* 📊 CHART */

function buildChart() {
  if (!chartCanvas.value) return

  const ctx = chartCanvas.value.getContext('2d')

  if (chartInstance) chartInstance.destroy()

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: Object.keys(currencyTotals.value),
      datasets: [{
        data: Object.values(currencyTotals.value),
        backgroundColor:
            user_type.value === 'corporate'
                ? ['#10B981','#3B82F6','#6366F1','#8B5CF6']
                : ['#6366F1','#A5B4FC','#C7D2FE']
      }]
    },
    options: {
      plugins: { legend: { display: false } },
      cutout: '75%'
    }
  })
}

/* 🔌 API */

async function fetchData() {
  const { data } = await api.get('/accounts',{
      headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`
  }})

  items.value = data

  const totals = {}

  data.forEach(acc => {
    if (!totals[acc.currency]) totals[acc.currency] = 0
    totals[acc.currency] += parseFloat(acc.available_balance || 0)
  })

  currencyTotals.value = totals
  buildChart()
}

/* 🕒 TIME */

onMounted(() => {
  const now = new Date()
  displayTime.value = now.toLocaleString('en-US', {
    hour12: true
  })


  user_type.value=JSON.parse(localStorage.getItem("user")).user_type;

  fetchData()
  fetchImages()
})
</script>


<style scoped>

/* smooth feel */
* {
  transition: all 0.25s ease;
}

/* glass effect (corporate) */
.bg-white\/5 {
  backdrop-filter: blur(12px);
}

/* premium hover */
.hover\:bg-indigo-50:hover {
  transform: translateY(-2px);
}

/* corporate hover */
.hover\:bg-white\/10:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}

/* scrollbar */
::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}


/* Base Badge */
.user-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: capitalize;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

/* Icon */
.type-icon {
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Text */
.type-text {
  position: relative;
  z-index: 2;
}

/* Glow effect */
.user-type-badge::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.4;
  filter: blur(12px);
  z-index: 0;
}

/* =========================
   CORPORATE (🔥 خیلی خفن)
========================= */
.type-corporate {
  background: linear-gradient(135deg, #f8fafc, #eef2ff);
  color: #1e293b;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.type-corporate::before {
  background: linear-gradient(135deg, #c7d2fe, #bae6fd);
}

.type-corporate:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

/* =========================
   PREMIUM (💎 لوکس)
========================= */
.type-premium {
  background: linear-gradient(135deg, #6366f1, #a855f7);
  color: white;
  box-shadow:
      0 4px 20px rgba(99, 102, 241, 0.6);
}

.type-premium::before {
  background: linear-gradient(135deg, #818cf8, #c084fc);
}

.type-premium:hover {
  transform: scale(1.05);
  box-shadow:
      0 10px 30px rgba(168, 85, 247, 0.7);
}

/* =========================
   STANDARD (مینیمال)
========================= */
.type-standard {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.type-standard::before {
  background: #d1d5db;
}

.type-standard:hover {
  background: #e5e7eb;
}

</style>